// useWxSDK.ts
// #ifdef WEB
import wx from 'weixin-js-sdk'
// #endif
import { getSDK } from '@/service/user/auth'
import { useAuthStore, useCommonStore, useTenantStore } from '@/store'
import { buildHashUrl } from '@/utils/common'

// 文档 https://developers.weixin.qq.com/doc/offiaccount/OA_Web_Apps/JS-SDK.html
// 实时该项目用到的微信sdk服务有：
// 1. 分享功能，包括分享到朋友圈和分享到好友
// 2. 获取地理位置
// 3. 打开地图
// 4. 微信扫码
// 5. 微信菜单
// 6. 开放标签

/** 业务实际使用的 JS 接口；初始化时一次性申请，避免后续页面重复 config。 */
const DEFAULT_JS_API_LIST: wx.jsApiList = [
  'updateTimelineShareData',
  'updateAppMessageShareData',
  'openLocation',
  'getLocation',
  'scanQRCode',
  'hideMenuItems',
  'showMenuItems',
]
const DEFAULT_OPEN_TAG_LIST: wx.openTagList = ['wx-open-launch-weapp']
/** 分享封面兜底图：必须是绝对地址，且微信只认 png/jpg。 */
const FALLBACK_SHARE_IMG = 'static/images/wyt.png'

export interface WxShareOptions {
  title?: string
  desc?: string
  link?: string
  imgUrl?: string
  success?: () => void
  fail?: (err: unknown) => void
}

// ---------------------------------------------------------------------------
// 模块级状态：wx.config 作用于整个 document，不是某个组件实例，
// 因此初始化状态、并发控制、分享队列都不能放在 useWxSdk() 内部。
// ---------------------------------------------------------------------------
const error = ref<unknown>(null)
const loading = ref(false)
const isInitialized = ref(false)
const initializedApis = new Set<string>()
const initializedOpenTags = new Set<string>()
let configuredUrl = ''
let configTask: Promise<void> | null = null

let shareSeq = 0
let latestShare: { seq: number; payload: WxSharePayload } | null = null
let lastConfiguredUrl = ''
let appliedShareKey = ''
let logoTask: Promise<string | null> | null = null

interface WxSharePayload {
  app: wx.IupdateAppMessageShareData
  timeline: wx.IupdateTimelineShareData
}

interface JsSdkConfigData {
  timestamp: number | string
  nonceStr: string
  signature: string
}

/** 项目 TypeScript 4.9 还没有 Promise.withResolvers，这里保持同样的线性写法。 */
function withResolvers<T>() {
  let resolve!: (value: T | PromiseLike<T>) => void
  let reject!: (reason?: unknown) => void
  const promise = new Promise<T>((res, rej) => {
    resolve = res
    reject = rej
  })
  return { promise, resolve, reject }
}

const getAbsoluteUrl = (url: string) => {
  if (/^https?:\/\//i.test(url)) return url
  if (!url) return location.href.split('#')[0]
  // 相对路径按当前站点解析，hash 路由的 #/pages/... 也能正确拼成绝对地址
  return new URL(url, location.href).href
}

const getFallbackShareImg = () => {
  const base = import.meta.env.BASE_URL || '/'
  const path = base.endsWith('/') ? `${base}${FALLBACK_SHARE_IMG}` : `${base}/${FALLBACK_SHARE_IMG}`
  return `${location.origin}${path}`
}

const requestJsSdkConfig = async (
  url: string,
  jsApiList: wx.jsApiList,
  openTagList: wx.openTagList,
): Promise<JsSdkConfigData> => {
  const res = await getSDK({ url, jsApiList, openTagList })
  if (res.code !== 200) {
    throw new Error('getSDK接口异常: ' + res.message)
  }
  return res.data as JsSdkConfigData
}

/** wx.config 是异步校验；必须等 ready 后才能调用业务接口。 */
const applyJsSdkConfig = (
  data: JsSdkConfigData,
  jsApiList: wx.jsApiList,
  openTagList: wx.openTagList,
) => {
  const { promise, resolve, reject } = withResolvers<void>()
  let settled = false

  wx.config({
    debug: false,
    appId: import.meta.env.VITE_WX_APPID,
    timestamp: Number(data.timestamp),
    nonceStr: data.nonceStr,
    signature: data.signature,
    jsApiList,
    openTagList,
  })

  wx.ready(() => {
    if (settled) return
    settled = true
    resolve()
  })

  wx.error((err) => {
    console.error('wx.error微信JS-SDK初始化失败:', err)
    if (settled) return
    settled = true
    reject(err)
  })

  return promise
}

const configure = async (
  url: string,
  jsApiList: wx.jsApiList,
  openTagList: wx.openTagList,
): Promise<void> => {
  loading.value = true
  try {
    const data = await requestJsSdkConfig(url, jsApiList, openTagList)
    // wx.config 会把 getLocation 等别名原地改成 geoLocation，不能让它污染下面记录的状态。
    await applyJsSdkConfig(data, [...jsApiList], [...openTagList])

    configuredUrl = url
    jsApiList.forEach((api) => initializedApis.add(api))
    openTagList.forEach((tag) => initializedOpenTags.add(tag as string))
    error.value = null
    isInitialized.value = true
    appliedShareKey = ''
  } catch (err) {
    error.value = err
    throw err
  } finally {
    loading.value = false
  }
}

/**
 * 初始化微信JS-SDK并动态添加所需的jsApiList。
 * 并发调用会复用同一个 config 任务，避免多次 wx.config 互相覆盖；
 * 业务页面无需等待 App.vue 的初始化完成，直接调用分享即可。
 */
const initWxSdk = async (
  requiredJsApiList: wx.jsApiList = DEFAULT_JS_API_LIST,
  openTagList: wx.openTagList = DEFAULT_OPEN_TAG_LIST,
): Promise<void> => {
  // 非微信浏览器没有 WeixinJSBridge，wx.ready 永远不会触发，直接跳过。
  if (typeof navigator === 'undefined' || !/MicroMessenger/i.test(navigator.userAgent)) return

  // 等待正在进行的初始化；失败时继续往下走，允许本次重新发起一次。
  while (configTask) {
    try {
      await configTask
    } catch {
      // 错误已记录到 error，由后续流程决定是否重试
    }
  }

  const url = location.href.split('#')[0]
  if (
    isInitialized.value &&
    configuredUrl === url &&
    requiredJsApiList.every((api) => initializedApis.has(api)) &&
    openTagList.every((tag) => initializedOpenTags.has(tag))
  ) {
    return
  }

  // 重复调用 wx.config 时 jsApiList 会整体替换，必须带上已声明的全部接口。
  const jsApiList = [...new Set([...initializedApis, ...requiredJsApiList])] as wx.jsApiList
  const mergedOpenTags = [...new Set([...initializedOpenTags, ...openTagList])] as wx.openTagList

  const task = configure(url, jsApiList, mergedOpenTags)
  configTask = task
  try {
    await task
  } finally {
    // 只清理自己创建的任务，防止并发调用互相抹掉状态。
    if (configTask === task) configTask = null
  }
}

const applySharePayload = (payload: WxSharePayload) => {
  if (!isInitialized.value) return
  const key = [payload.app.title, payload.app.desc, payload.app.link, payload.app.imgUrl].join(
    '\u0001',
  )
  if (key === appliedShareKey) return
  appliedShareKey = key
  wx.updateAppMessageShareData(payload.app)
  wx.updateTimelineShareData(payload.timeline)
}

/**
 * 微信分享功能。
 * - 未初始化完成时先记录最新一次分享内容，wx.ready 后自动补发；
 * - 同一次页面加载内多次调用只生效最后一次，避免旧数据覆盖新数据；
 * - 不再等待 logo 请求，封面缺失时先用兜底图，logo 返回后静默刷新。
 */
const shareContent = async (
  options: WxShareOptions = {},
  successCallback: () => void = () => {},
  failCallback: (err: unknown) => void = (err) => console.error(err),
) => {
  if (typeof navigator === 'undefined' || !/MicroMessenger/i.test(navigator.userAgent)) return

  const seq = ++shareSeq
  lastConfiguredUrl = location.href
  const tenantStore = useTenantStore()
  const commonStore = useCommonStore()
  const onSuccess = options.success || successCallback
  const onFail = options.fail || failCallback

  const defaultLink = buildHashUrl(location.href, {
    tenant_id: tenantStore.getTenantData('tenantId'),
  })
  const link = getAbsoluteUrl(options.link || defaultLink)
  const title = options.title || '稳安特智慧社区'
  const desc = options.desc || '稳安特智慧社区'
  const cachedLogo = commonStore.getCommonData('logoUrl')
  const imgUrl = options.imgUrl || cachedLogo || getFallbackShareImg()

  const payload: WxSharePayload = {
    app: {
      title,
      desc,
      link,
      imgUrl,
      success: onSuccess,
      fail: onFail,
    },
    timeline: {
      title,
      link,
      imgUrl,
      success: onSuccess,
      fail: onFail,
    },
  }
  latestShare = { seq, payload }

  // 封面图未就绪时后台加载，不阻塞分享配置；加载完成后仅刷新封面。
  if (!options.imgUrl && !cachedLogo) {
    if (!logoTask) {
      const { getLogoImg } = useLogo()
      logoTask = getLogoImg().catch((err) => {
        console.error('获取分享封面失败:', err)
        return null
      })
    }
    logoTask.then((url) => {
      if (!url || latestShare?.seq !== seq || !isInitialized.value) return
      const nextImgUrl = getAbsoluteUrl(url)
      latestShare.payload.app.imgUrl = nextImgUrl
      latestShare.payload.timeline.imgUrl = nextImgUrl
      applySharePayload(latestShare.payload)
    })
  }

  try {
    await initWxSdk()
    // 等待期间可能有更新的分享内容，只有最后一次调用有权写入。
    if (!isInitialized.value || latestShare?.seq !== seq) return
    applySharePayload(latestShare.payload)
  } catch (err) {
    error.value = err
    onFail(err)
  }
}

/** SDK 就绪后，若当前页面没有登记过分享内容，补一份默认分享。 */
export const ensureDefaultShare = () => {
  if (!latestShare) shareContent({})
}

/**
 * 路由切换后，把分享内容恢复成当前页面的默认值。
 * 微信分享配置是文档级的，SPA 切换页面不会自动清空，否则会沿用上一页的标题和链接。
 */
export const resetShareForCurrentPage = () => {
  if (!lastConfiguredUrl) return
  if (location.href === lastConfiguredUrl) return
  shareContent({})
}

export function useWxSdk() {
  const authStore = useAuthStore() // 获取Pinia中的用户授权信息

  /**
   * 获取地理位置信息
   * @returns {Promise<Object>} 返回包含纬度和经度的地理位置信息
   */
  const getLocation = async () => {
    // 页面可能在 wx.ready 之前就取位置，先等 SDK 就绪，否则调用会静默失败。
    await initWxSdk()
    return new Promise((resolve, reject) => {
      wx.getLocation({
        type: 'gcj02',
        success: (res) => {
          const loc = {
            longitude: +res.longitude,
            latitude: +res.latitude,
          }
          // 更新Pinia中的位置
          authStore.setCurLoc(loc)
          console.log('获取位置成功', loc)
          resolve({ ...loc })
        },
        fail: (err) => {
          console.error('获取地理位置失败:', err)
          reject(err)
        },
      })
    })
  }

  /**
   * 打开腾讯地图并定位
   * @param {Object} placeInfo - 包含门店位置的配置信息
   */
  const openTencentMap = async (placeInfo) => {
    await initWxSdk()
    wx.openLocation({
      latitude: +placeInfo.latitude,
      longitude: +placeInfo.longitude,
      name: placeInfo.name,
      address: placeInfo.address,
      scale: 18,
      infoUrl: '',
    })
  }

  /**
   * 微信扫码功能
   * @param {Function} fn - 处理扫码结果的回调函数
   */
  const wxScanCode = async (fn) => {
    await initWxSdk()
    wx.scanQRCode({
      needResult: 1,
      scanType: ['qrCode', 'barCode'],
      success: function (res) {
        const resultStr = res.resultStr
        fn(resultStr) // 调用传入的回调函数处理扫描结果
      },
      fail: (error) => {
        console.error('扫码失败:', error)
      },
    })
  }

  /**
   * 隐藏指定的微信菜单项
   */
  const hideMenuItems = async () => {
    await initWxSdk()
    // 调用 wx.hideMenuItems API 隐藏菜单项
    wx.hideMenuItems({
      menuList: [
        'menuItem:share:appMessage', // 隐藏发送给朋友的分享菜单项
        'menuItem:share:timeline', // 隐藏分享到朋友圈的分享菜单项
        'menuItem:share:qq', // 隐藏分享到QQ的分享菜单项
        'menuItem:share:weiboApp', // 隐藏分享到微博的分享菜单项
        'menuItem:share:facebook', // 隐藏分享到Facebook的分享菜单项
        'menuItem:share:QZone', // 隐藏分享到QQ空间的分享菜单项
        'menuItem:favorite', // 隐藏收藏菜单项
        'menuItem:copyUrl', // 隐藏复制链接的菜单项
      ],
    })
  }

  /**
   * 显示指定的微信菜单项
   */
  const showMenuItems = async () => {
    await initWxSdk()
    // 调用 wx.showMenuItems API 显示菜单项
    wx.showMenuItems({
      menuList: [
        'menuItem:share:appMessage', // 显示发送给朋友的分享菜单项
        'menuItem:share:timeline', // 显示分享到朋友圈的分享菜单项
        'menuItem:share:qq', // 显示分享到QQ的分享菜单项
        'menuItem:share:weiboApp', // 显示分享到微博的分享菜单项
        'menuItem:share:facebook', // 显示分享到Facebook的分享菜单项
        'menuItem:share:QZone', // 显示分享到QQ空间的分享菜单项
        'menuItem:favorite', // 显示收藏菜单项
        'menuItem:copyUrl', // 显示复制链接的菜单项
      ],
    })
  }

  return {
    initWxSdk,
    shareContent,
    getLocation,
    openTencentMap,
    wxScanCode,
    showMenuItems,
    hideMenuItems,
    error,
    loading,
    isInitialized,
  }
}
